import {
  Badge,
  Box,
  Button,
  Drawer,
  DrawerContent,
  DrawerOverlay,
  Flex,
  LinkBox,
  LinkOverlay,
  VStack,
  useDisclosure
} from "@chakra-ui/react"
import { useIsScrolled } from "@pathwright/utils/src/scroll/useIsScrolled"
import CardLink from "@pathwright/ux/src/components/card/CardLink"
import CardLinks from "@pathwright/ux/src/components/card/CardLinks"
import Link from "@pathwright/ux/src/components/link/Link"
import Pathicon from "@pathwright/ux/src/components/pathicon/Pathicon"
import { PageFragment, usePagesQuery } from "../../api/generated"
import AdaptedSignedInContainer from "../../auth/AdaptedSignedInContainer"
import { usePathwrightContext } from "../../pathwright/PathwrightContext"
import { useCanViewSimpleNav } from "../../unified-home/useCanViewUnifiedHome"
import { canAdministrate } from "../../user/permissions"
import { flattenEdges } from "../../utils/apollo"
import SpaceSwitcher from "../switcher/SpaceSwitcher"
import SpaceAugmentationIdentity from "./SpaceAugmentationIdentity"

function InnerSpaceAugmentationDrawer() {
  const pwContext = usePathwrightContext()
  const { school, me } = pwContext
  const disclosure = useDisclosure()

  const pagesQuery = usePagesQuery({
    variables: { first: 25, isLive: true, showInNav: true },
    skip: !disclosure.isOpen
  })
  const pages = (flattenEdges(pagesQuery.data?.school?.pages) ||
    []) as PageFragment[]

  const [isScrolled, setContainerRef] = useIsScrolled()

  return (
    <>
      <Box
        className="SpaceAugmentationDrawer-Identity group"
        as="button"
        cursor="pointer"
        bg="none"
        pos="fixed"
        top="1rem"
        left="1rem"
        zIndex="overlay"
        transition="transform 200ms ease-in-out"
        _hover={{
          transform: "scale(1.1)"
        }}
        {...disclosure.getButtonProps()}
        onClick={disclosure.onToggle}
      >
        <SpaceAugmentationIdentity
          boxShadow="lg"
          image={school.image!}
          name={school.name}
        />
        <Flex
          pos="absolute"
          bottom={0}
          right={0}
          transition="transform 200ms ease-in-out"
          transform="translate(10%, 10%)"
          _groupHover={{
            transform: "translate(10%, 30%)"
          }}
          bg="white"
          p={1}
          borderRadius="full"
          align="center"
          justify="center"
          boxShadow="lg"
          boxSize=".8rem"
        >
          <Pathicon icon="chevron-down" color="brand.500" size=".8em" />
        </Flex>
      </Box>
      <Drawer
        isOpen={disclosure.isOpen}
        onClose={disclosure.onClose}
        placement="left"
        size="sm"
      >
        <DrawerOverlay bg="blackAlpha.400" />
        <DrawerContent
          bg="whiteAlpha.600"
          backdropFilter="blur(20px)"
          borderRadius="20px"
          inset=".5rem !important"
          // Fading in from top left (matches assumed animation from UI with down arrow).
          motionProps={{
            initial: { opacity: 0, x: -20, y: -20 },
            animate: { opacity: 1, x: 0, y: 0 },
            exit: { opacity: 0, x: -20, y: -20 },
            transition: { duration: 0.25 }
          }}
        >
          <VStack
            ref={setContainerRef}
            overflow="auto"
            align="start"
            p={2}
            h="100%"
          >
            <Box
              w="100%"
              pos="sticky"
              top={0}
              zIndex={1}
              bg={isScrolled ? "whiteAlpha.900" : "transparent"}
              transition="background-color 200ms ease-in-out"
              borderRadius="card"
            >
              <SpaceSwitcher />
            </Box>

            <CardLinks>
              <CardLink
                to="/library/"
                onClick={disclosure.onClose}
                label="Library"
                icon="book"
              />
              {pages.map((page) => (
                <CardLink
                  key={page.id}
                  to={page.url.startsWith("/") ? page.url : undefined}
                  href={page.url.startsWith("/") ? undefined : page.url}
                  onClick={disclosure.onClose}
                  label={page.name}
                  icon="newspaper"
                />
              ))}
              <CardLink
                to="/help/"
                onClick={disclosure.onClose}
                label="Help"
                icon="question-mark"
              />
            </CardLinks>

            {canAdministrate(pwContext) && (
              <VStack
                bg="whiteAlpha.500"
                borderRadius="card"
                p={2}
                w="100%"
                align="start"
              >
                <Badge colorScheme="blackAlpha" mx={2} mt={1}>
                  Admin
                </Badge>
                <CardLinks>
                  <CardLink
                    to="/dashboard/"
                    onClick={disclosure.onClose}
                    label="Dashboard"
                    icon="gauge-square"
                  />
                  <CardLink
                    to="/manage/school/"
                    onClick={disclosure.onClose}
                    label="Space Settings"
                    icon="gear"
                  />
                  <CardLink
                    to="/community/members/"
                    onClick={disclosure.onClose}
                    label="Staff & Members"
                    icon="multi-user"
                  />
                  <CardLink
                    onClick={() => window.Intercom?.("showNewMessage")}
                    label="Pathwright Help"
                    icon="question-mark"
                  />
                  {!school.is_activated && (
                    <Link
                      as={Button}
                      to="/pricing/"
                      leftIcon={<Pathicon icon="rocket" ml="auto" />}
                      onClick={disclosure.onClose}
                    >
                      Activate Now
                    </Link>
                  )}
                </CardLinks>
              </VStack>
            )}
            <Box mt="auto" w="100%" pos="sticky" zIndex={1} bottom={0}>
              {me ? (
                <Box
                  bg="whiteAlpha.800"
                  borderRadius="card"
                  w="100%"
                  pos="relative"
                  sx={{ ".AuthWrapper": { px: 4, py: 2 } }}
                >
                  <LinkBox w="100%">
                    <Link
                      as={LinkOverlay}
                      to="/home"
                      w="100%"
                      onClick={disclosure.onClose}
                    >
                      <AdaptedSignedInContainer size="md" />
                    </Link>
                  </LinkBox>
                </Box>
              ) : (
                <Link
                  as={Button}
                  colorScheme="brand"
                  variant="solid"
                  width="stretch"
                  to="/auth/sign-in/"
                  onClick={disclosure.onClose}
                  leftIcon={<Pathicon icon="avatar-circle" />}
                  rightIcon={<Pathicon icon="chevron-right" />}
                >
                  Sign In
                </Link>
              )}
            </Box>
          </VStack>
        </DrawerContent>
      </Drawer>
    </>
  )
}

function SpaceAugmentationDrawer() {
  const canViewUnifiedHome = useCanViewSimpleNav()
  return canViewUnifiedHome ? <InnerSpaceAugmentationDrawer /> : null
}

export default SpaceAugmentationDrawer
